import React, { useState, useMemo, useEffect } from "react";
import { useDeal } from "../lib/store";
import { streamPost, scoutApi } from "../lib/api";
import { Card } from "../components/shared";
import { toast } from "sonner";
import { intelApi } from "../lib/api";
import { History, ExternalLink, Eye, Plus, Landmark, Users2 } from "lucide-react";

const TYPES = ["Roll-up", "PE platform", "Add-on", "Independent sponsor", "Recapitalization"];

const parseDeals = (full) => {
  const clean = (full || "").replace(/```json/gi, "").replace(/```/g, "").trim();
  try {
    const s = clean.indexOf("{"), e = clean.lastIndexOf("}");
    const arr = JSON.parse(clean.slice(s, e + 1)).deals;
    if (Array.isArray(arr) && arr.length) return arr;
  } catch (_) {}
  const out = [];
  const re = /\{[^{}]*\}/g; let mm;
  while ((mm = re.exec(clean))) { try { const o = JSON.parse(mm[0]); if (o && (o.acquirer || o.target)) out.push(o); } catch (_) {} }
  return out;
};

const searchUrl = (d) => `https://www.google.com/search?q=${encodeURIComponent(`${d.acquirer || ""} ${d.target || ""} acquisition ${d.period || ""}`.trim())}`;
const tone = (t) => (t === "Roll-up" || t === "PE platform" ? "go" : t === "Independent sponsor" ? "amber" : "");

export default function ClosedDeals() {
  const { industry: dealIndustry } = useDeal();
  const [industry, setIndustry] = useState(dealIndustry);
  useEffect(() => { setIndustry(dealIndustry); }, [dealIndustry]);
  const [thesis, setThesis] = useState("");
  const [sel, setSel] = useState(() => Object.fromEntries(TYPES.map((t) => [t, true])));
  const [deals, setDeals] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [watched, setWatched] = useState(new Set());

  const consolidators = useMemo(() => {
    const map = {};
    deals.forEach((d) => {
      const k = (d.acquirer || "").trim();
      if (!k) return;
      if (!map[k]) map[k] = { acquirer: k, count: 0, types: new Set(), sponsorType: d.sponsorType, thesis: d.thesis };
      map[k].count++; if (d.dealType) map[k].types.add(d.dealType);
    });
    return Object.values(map).map((x) => ({ ...x, types: [...x.types] })).sort((a, b) => b.count - a.count);
  }, [deals]);

  const watchAcquirer = async (name, note, thesis) => {
    const key = (name || "").toLowerCase().trim();
    if (!name || watched.has(key)) return;
    await scoutApi.create({
      name: `${name} (watch)`, businessType: industry, source: "Closed-deal intel",
      status: "Watching", summary: thesis || "", notes: note || `Active consolidator in ${industry}`,
    });
    setWatched((s) => new Set([...s, key]));
    toast.success(`Watching ${name} — added to your Deal Tracker`);
  };

  const addToCrm = async (audience, name, note, type) => {
    if (!name) return;
    try {
      const res = await intelApi.promote({ audience, rows: [{ firm: name, name, type: type || "", thesis: note || "", why: note || "" }] });
      toast.success(res.added.length
        ? `${name} added to ${audience === "lenders" ? "Debt Lenders" : "Investors"}`
        : `${name} is already in the ${audience === "lenders" ? "lender" : "investor"} list`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add them"); }
  };

  const addToCapital = async (name, note) => {
    if (!name) return;
    try {
      const res = await intelApi.toPipeline({
        audience: "investors", folder: `Closed-deal intel — ${industry || "market"}`, dealName: industry || "",
        rows: [{ name, firm: name, why: note || "" }],
      });
      toast.success(res.added.length ? `${name} saved to Capital Markets` : `${name} is already in the pipeline`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save them"); }
  };

  const scan = async () => {
    setScanning(true); setDeals([]);
    try {
      let full = "";
      await streamPost("/ai/closed-deals", { industry, thesis, dealTypes: TYPES.filter((t) => sel[t]) }, (c, f) => { full = f; });
      const arr = parseDeals(full);
      setDeals(arr);
      if (arr.length) toast.success(`Surfaced ${arr.length} closed deals`);
      else toast.error("No deals surfaced — refine the thesis and retry");
    } catch { toast.error("Closed-deal scan failed — try again"); }
    finally { setScanning(false); }
  };

  return (
    <div className="page" data-testid="page-closed">
      <div className="page-head">
        <div className="page-title">Closed Deal Scout</div>
        <div className="page-sub">Recently closed roll-ups, PE platform builds & independent-sponsor deals nationwide — matched to your thesis</div>
      </div>

      <div className="banner info" data-testid="closed-disclaimer"><span>ℹ</span><span>AI-surfaced precedent transactions modeled on known market activity — <b>verify each via its source</b> before relying on figures.</span></div>

      <Card title="Search Closed Deals" testid="closed-filters" right={<button className="btn btn-primary" onClick={scan} disabled={scanning} data-testid="closed-run"><History size={13} style={{ marginRight: 4 }} />{scanning ? "Scanning…" : "Scan Closed Deals"}</button>}>
        <div className="grid g2">
          <div className="field"><label>Industry target</label><input className="inp" value={industry} onChange={(e) => setIndustry(e.target.value)} placeholder="Set the deal industry on the Assumptions page" data-testid="closed-industry" /></div>
          <div className="field"><label>Thesis / angle (optional)</label><input className="inp" placeholder="e.g. regional density plays, recurring-revenue roll-ups, founder-owned add-ons" value={thesis} onChange={(e) => setThesis(e.target.value)} data-testid="closed-thesis" /></div>
        </div>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 4 }}>
          <span style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text2)" }}>Deal types</span>
          {TYPES.map((t) => (
            <label key={t} style={{ display: "flex", gap: 6, fontSize: 11.5, alignItems: "center", cursor: "pointer" }}>
              <input type="checkbox" checked={!!sel[t]} onChange={(e) => setSel({ ...sel, [t]: e.target.checked })} data-testid={`closed-type-${t.replace(/[^a-z0-9]/gi, "-")}`} /> {t}
            </label>
          ))}
        </div>
      </Card>

      {consolidators.length > 0 && (
        <Card title={`Consolidator Map — who's rolling up ${industry} fastest`} style={{ marginTop: 12 }} testid="consolidator-map">
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {consolidators.map((c, i) => {
              const isWatched = watched.has(c.acquirer.toLowerCase().trim());
              const note = `Active consolidator — ${c.count} closed deal${c.count === 1 ? "" : "s"} surfaced in ${industry}`;
              return (
                <div key={c.acquirer} className="cd-cons" data-testid="consolidator-row">
                  <span className="cd-cons-rank">{i + 1}</span>
                  <div className="cd-cons-id">
                    <b>{c.acquirer}</b>
                    <span>{c.sponsorType || "—"}{c.types.length ? ` · ${c.types.join(", ")}` : ""}</span>
                  </div>
                  <div className="cd-cons-bar" title={`${c.count} closed deals surfaced`}>
                    <i style={{ width: `${(c.count / consolidators[0].count) * 100}%` }} />
                  </div>
                  <span className="cd-cons-count">{c.count} deal{c.count === 1 ? "" : "s"}</span>
                  <div className="cd-cons-acts">
                    <button className="btn btn-sm" onClick={() => addToCrm("investors", c.acquirer, c.thesis || note, c.sponsorType)} title="Add to the Investors list" data-testid={`cons-add-investor-${i}`}><Users2 size={11} />Investor</button>
                    <button className="btn btn-sm" onClick={() => addToCrm("lenders", c.acquirer, c.thesis || note, c.sponsorType)} title="Add to Debt Lenders" data-testid={`cons-add-lender-${i}`}><Landmark size={11} />Lender</button>
                    <button className="btn btn-sm" onClick={() => addToCapital(c.acquirer, c.thesis || note)} title="Save to Capital Markets pipeline" data-testid={`cons-add-capital-${i}`}><Plus size={11} />Capital Markets</button>
                    <button className="btn btn-sm" onClick={() => watchAcquirer(c.acquirer, note, c.thesis)} disabled={isWatched} data-testid={`watch-consolidator-${i}`}><Eye size={12} />{isWatched ? "Watching" : "Watch"}</button>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {(scanning || deals.length > 0) && (
        <Card title={`Closed Deals${deals.length ? ` (${deals.length})` : ""}`} style={{ marginTop: 12 }} testid="closed-results">
          {scanning && <div className="bullet">Scanning recent nationwide transactions for roll-ups, platforms and sponsor closings matching your thesis…</div>}
          <div className="grid g2">
            {deals.map((d, i) => (
              <div key={i} className="card" style={{ background: "var(--bg2)", border: "1px solid var(--border)" }} data-testid="closed-card">
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{d.acquirer} <span style={{ color: "var(--text3)", fontWeight: 400 }}>→</span> {d.target}</div>
                    <div style={{ fontSize: 11.5, color: "var(--text2)" }}>{d.industry} · {d.period}</div>
                  </div>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
                    <span className={`pill ${tone(d.dealType)}`} style={{ fontSize: 9.5 }}>{d.dealType}</span>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 12px", fontSize: 11.5, marginTop: 8 }}>
                  <div><span style={{ color: "var(--text3)" }}>Size: </span>{d.size || "undisclosed"}</div>
                  <div><span style={{ color: "var(--text3)" }}>Sponsor: </span>{d.sponsorType || "—"}</div>
                </div>
                {d.thesis && <div className="bullet" style={{ marginTop: 6 }}>{d.thesis}</div>}
                <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 4 }}>Source: {d.source || "—"}</div>
                <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <a className="btn" href={searchUrl(d)} target="_blank" rel="noreferrer" data-testid={`closed-view-${i}`}><ExternalLink size={13} style={{ marginRight: 4 }} />Verify / Read More</a>
                  <button className="btn" onClick={() => watchAcquirer(d.acquirer, `From closed deal: ${d.acquirer} → ${d.target} (${d.period})`, d.thesis)} disabled={watched.has((d.acquirer || "").toLowerCase().trim())} data-testid={`closed-watch-${i}`}><Eye size={13} style={{ marginRight: 4 }} />{watched.has((d.acquirer || "").toLowerCase().trim()) ? "Watching" : "Watch Acquirer"}</button>
                  <button className="btn" onClick={() => addToCrm("investors", d.acquirer, d.thesis, d.sponsorType)} title="Add the acquirer to the Investors list" data-testid={`closed-add-investor-${i}`}><Plus size={13} style={{ marginRight: 4 }} />Investor</button>
                  <button className="btn" onClick={() => addToCrm("lenders", d.acquirer, d.thesis, d.sponsorType)} title="Add the acquirer to Debt Lenders" data-testid={`closed-add-lender-${i}`}><Plus size={13} style={{ marginRight: 4 }} />Lender</button>
                  <button className="btn" onClick={() => addToCapital(d.acquirer, d.thesis)} title="Save to the Capital Markets pipeline" data-testid={`closed-add-capital-${i}`}><Plus size={13} style={{ marginRight: 4 }} />Capital Markets</button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
